import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getProductListDataView,
    getProductListDataViewProperty,
} from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LazyImage from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import parse from "html-react-parser";
import truncate from "lodash/truncate";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const correctedQuery = getProductListDataViewProperty(state, "correctedQuery");
    const { productFilters } = state.pages.productList;
    return {
        query: correctedQuery || productFilters.query,
        productListLoading: state.pages.productList.isLoading,
        queryString: state.pages.productList.productFilters.query,
        productsDataView: getProductListDataView(state),
    };
};

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps>;

export interface CENSearchResultBoxStyles {
    container?: InjectableCss;
    title?: InjectableCss;
    titleWrapper?: InjectableCss;
    productTitle?: InjectableCss;
    productDescription?: TypographyProps;
    searchResultSeperator?: InjectableCss;
    searchImageStyles: InjectableCss;
    mainContainer: InjectableCss;
    productDescriptionMobile: TypographyProps;
}

export const cenSearchResultBoxStyles: CENSearchResultBoxStyles = {
    container: {
        css: css`
            border: 4px solid ${COLORS.background.grayShade};
            background: ${COLORS.white};
            width: 100%;
            height: 100%;
            padding: 24px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            padding: 16px;
                            width: 97%;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    titleWrapper: {
        css: css`
            margin-bottom: 8px;
        `,
    },
    title: {
        css: css`
            font-size: ${TYPOGRAPHY.size.base};
            font-weight: ${TYPOGRAPHY.weight.base};
            line-height: ${TYPOGRAPHY.lineHeight.base};
            color: ${getProp("theme.typography.p.color")};
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            font-size: ${TYPOGRAPHY.size.smaller};
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    productTitle: {
        css: css`
            font-size: ${TYPOGRAPHY.size.base};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: ${TYPOGRAPHY.lineHeight.base};
            color: ${getProp("theme.typography.p.color")};
            margin-bottom: 4px;
            margin-left: 20px;
            text-align: left;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            font-size: ${TYPOGRAPHY.size.smaller};
                            line-height: 16px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    productDescription: {
        variant: "p",
        css: css`
            &,
            p {
                font-size: ${TYPOGRAPHY.size.smaller} !important;
                color: ${getProp("theme.typography.p.color")};
                line-height: 20px;
                margin-top: 8px;
                margin-left: 20px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            text-align: left;
                            display: none;
                        `,
                        css`
                            text-align: left;
                            display: none;
                        `,
                        css`
                            display: none;
                        `,
                        null,
                        null,
                    ])}
            }
        `,
    },
    productDescriptionMobile: {
        variant: "p",
        css: css`
            &,
            p {
                font-size: ${TYPOGRAPHY.size.smaller} !important;
                color: ${getProp("theme.typography.p.color")};
                line-height: 20px;
                margin-top: 8px;
                margin-left: 20px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            text-align: left;
                            display: block;
                            margin-left: 0px;
                        `,
                        css`
                            text-align: left;
                            display: block;
                            margin-left: 0px;
                        `,
                        css`
                            display: block;
                            margin-left: 0px;
                        `,
                        css`
                            display: none;
                        `,
                        css`
                            display: none;
                        `,
                    ])}
            }
        `,
    },
    searchResultSeperator: {
        css: css`
            width: 64px;
            height: 1px;
            background: ${COLORS.primary.base};
            margin-bottom: 16px;
        `,
    },
    searchImageStyles: {
        css: css`
            max-width: 145px;
            box-shadow: 0px 0px 1px rgb(38 37 37 / 56%), 0px 2px 8px rgb(38 37 37 / 16%);
            > img {
                width: 100%;
                height: auto;
                object-fit: contain;
                aspect-ratio: 1/1;
            }
        `,
    },
    mainContainer: {
        css: css`
            display: flex;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        align-items: center;
                        text-align: left;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
};

const styles = cenSearchResultBoxStyles;
const CENSearchResultBox: React.FC<Props> = ({ productsDataView, query, queryString }) => {
    const firstLoad = React.useRef(true);

    const productsAvailable =
        productsDataView &&
        !productsDataView.isLoading &&
        productsDataView.value !== undefined &&
        productsDataView.value.length > 0;

    if (!query || !productsAvailable) {
        return null;
    }

    const finalProductObj = productsDataView?.value?.[0];

    return (
        <>
            {query && (
                <StyledWrapper {...styles.container}>
                    <StyledWrapper {...styles.titleWrapper}>
                        <Typography {...styles.title}>{`${translate("Top result for ")}"${query}"`}</Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.searchResultSeperator}></StyledWrapper>
                    <StyledWrapper {...styles.mainContainer}>
                        {finalProductObj?.images && finalProductObj?.images.length > 0 ? (
                            <LazyImage
                                src={finalProductObj?.images[0]?.smallImagePath}
                                {...styles.searchImageStyles}
                                altText={finalProductObj?.productTitle}
                            ></LazyImage>
                        ) : (
                            <LazyImage
                                src={
                                    finalProductObj?.brand?.logoSmallImagePath
                                        ? finalProductObj?.brand?.logoSmallImagePath
                                        : finalProductObj?.brand?.logoLargeImagePath
                                }
                                {...styles.searchImageStyles}
                                altText={finalProductObj?.productTitle}
                            >
                                {" "}
                            </LazyImage>
                        )}
                        <StyledWrapper>
                            <Link href={finalProductObj?.canonicalUrl} {...styles.productTitle}>
                                {finalProductObj?.productTitle}
                            </Link>
                            {finalProductObj && finalProductObj?.content && (
                                <Typography {...styles.productDescription}>
                                    {parse(
                                        transformContentHtml(
                                            truncate(finalProductObj?.content.htmlContent, {
                                                length: 250,
                                            }),
                                        ),
                                        parserOptions,
                                    )}
                                </Typography>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper>
                        {finalProductObj && finalProductObj?.content && (
                            <Typography {...styles.productDescriptionMobile}>
                                {parse(
                                    transformContentHtml(
                                        truncate(finalProductObj?.content.htmlContent, {
                                            length: 200,
                                        }),
                                    ),
                                    parserOptions,
                                )}
                            </Typography>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProduct(CENSearchResultBox)),
    definition: {
        group: "Categories",
        icon: "list-ul",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
